import { useState, useEffect, useRef, useMemo } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { useCart } from "@/contexts/CartContext";
import { ShoppingBag, ChevronLeft, Loader2, ChevronRight, X } from "lucide-react";
import DOMPurify from "dompurify";
import RecentlyViewed, { trackRecentlyViewed } from "@/components/RecentlyViewed";
import { trackViewContent } from "@/lib/meta";
import { ga4ViewItem } from "@/lib/ga4";
import SizeGuideModal from "@/components/SizeGuideModal";
import SEO from "@/components/SEO";
import ProductCard from "@/components/ProductCard";
import PdpBuyExtras from "@/components/PdpBuyExtras";
import ShopTheSet from "@/components/ShopTheSet";
import GiftCardProduct from "@/components/GiftCardProduct";
import { setPiecesFor } from "@/lib/products";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { formatProductName, productAlt, shopifyImg, shopifySrcSet, SIZES_PDP_MAIN } from "@/lib/format";
import { SHIP_LINE, SAME_DAY_SHIP_LINE, SHOW_SIZE_GUIDE, NOT_PROTECTIVE_LINE, CONTACT_EMAIL, SITE_URL } from "@/lib/site";
import { shipsSameDay, isJewelry, isMysteryBox } from "@/lib/products";
import NotFoundPage from "@/pages/NotFoundPage";

// Shown after the shipping line in the "Shipping And Exchanges" accordion (all products).
const SHIPPING_TAIL = "Tracking is emailed when your order ships. Unworn, unwashed items with tags on can be exchanged within 30 days of delivery.";

const PRODUCT_CONTENT = {
  "racer-jersey": {
    details: "Motorsport-inspired racing jersey in all black with contrast piping and AERWEAR racing graphics. Lightweight, breathable athletic fabric built to move. Relaxed fit with dropped shoulders. Model is 6'0\" and wears size M.",
    sizeGuide: ["Relaxed fit. Size down for a closer fit.", "S: Chest 21 in, Length 28 in", "M: Chest 22.5 in, Length 29 in", "L: Chest 24 in, Length 30 in", "XL: Chest 25.5 in, Length 31 in", "(Chest measured armpit to armpit, laid flat.)"],
    care: "Machine wash cold, inside out. Hang dry. Do not iron graphics. Do not bleach.",
  },
  "racer-longsleeve": {
    details: "Long sleeve racing jersey in all black with contrast piping and AERWEAR racing graphics down the sleeves. Lightweight, breathable athletic fabric. Relaxed fit with ribbed cuffs. Model is 6'0\" and wears size M.",
    sizeGuide: ["Relaxed fit. Size down for a closer fit.", "S: Chest 21 in, Length 28 in, Sleeve 33 in", "M: Chest 22.5 in, Length 29 in, Sleeve 34 in", "L: Chest 24 in, Length 30 in, Sleeve 35 in", "XL: Chest 25.5 in, Length 31 in, Sleeve 36 in", "(Chest measured armpit to armpit, laid flat.)"],
    care: "Machine wash cold, inside out. Hang dry. Do not iron graphics. Do not bleach.",
  },
  "racer-track-pants": {
    details: "All black track pants with contrast side piping and AERWEAR racing branding. Elastic waistband with drawstring and a relaxed, straight leg. Built for motion. Model is 6'0\" and wears size M.",
    sizeGuide: ["Relaxed fit.", "S: Waist 28 to 30 in, Inseam 30 in", "M: Waist 31 to 33 in, Inseam 31 in", "L: Waist 34 to 36 in, Inseam 32 in", "XL: Waist 37 to 39 in, Inseam 32 in"],
    care: "Machine wash cold, inside out. Hang dry. Do not bleach.",
  },
  "nightcall-tee": {
    details: "Heavyweight black tee with NIGHTCALL graphic. Boxy, oversized fit with a thick ribbed collar. Model is 6'0\" and wears size M.",
    sizeGuide: ["Oversized fit. Size down for a regular fit.", "S: Chest 22 in, Length 27 in", "M: Chest 23.5 in, Length 28 in", "L: Chest 25 in, Length 29 in", "XL: Chest 26.5 in, Length 30 in", "(Chest measured armpit to armpit, laid flat.)"],
    care: "Machine wash cold, inside out. Hang dry. Do not iron graphics.",
  },
};

const DEFAULT_CONTENT = {
  details: null,
  sizeGuide: ["Relaxed fit. See measurements below or contact us for help choosing a size."],
  care: "Machine wash cold, inside out. Hang dry. Do not bleach.",
};

export default function ProductDetailPage() {
  const { id } = useParams();
  const { addItem } = useCart();
  const [product, setProduct] = useState(null);
  const [allProducts, setAllProducts] = useState([]);
  const [selectedSize, setSelectedSize] = useState("");
  const [selectedColor, setSelectedColor] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(true);
  const [showSizeGuide, setShowSizeGuide] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(null);
  const [activeImage, setActiveImage] = useState(0);
  const [showStickyATC, setShowStickyATC] = useState(false);
  const [sizeError, setSizeError] = useState(false);
  const [justAdded, setJustAdded] = useState(false);
  const [zoomOpen, setZoomOpen] = useState(false);
  const atcRef = useRef(null);

  useEffect(() => {
    setLoading(true);
    setActiveImage(0);
    const fetchProduct = api.get(`/shopify/products/${id}`);
    const fetchAll = api.get("/shopify/products").catch(() => ({ data: [] }));
    Promise.all([fetchProduct, fetchAll]).then(([prodRes, allRes]) => {
      const p = prodRes.data;
      setProduct(p); setAllProducts(allRes.data || []);
      trackRecentlyViewed(p); trackViewContent(p); ga4ViewItem(p);
      if (p.colors?.length) setSelectedColor(p.colors[0].name);
      if (p.variants?.length) setSelectedVariant(p.variants.find((v) => v.available) || p.variants[0]);
    }).catch(() => {}).finally(() => setLoading(false));
  }, [id]);

  useEffect(() => {
    if (!product?.variants?.length) return;
    const match = product.variants.find((v) => (!v.size || v.size === selectedSize) && (!v.color || v.color === selectedColor));
    setSelectedVariant(match || product.variants[0]);
  }, [selectedSize, selectedColor, product]);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => setShowStickyATC(!entry.isIntersecting), { threshold: 0 });
    const el = atcRef.current;
    if (el) observer.observe(el);
    return () => { if (el) observer.unobserve(el); };
  }, [product]);

  const sanitizedDescription = useMemo(() => product?.description_html ? DOMPurify.sanitize(product.description_html) : "", [product?.description_html]);

  const handleAddToCart = () => {
    if (!product) return;
    if (!selectedSize && product.sizes?.length > 0) { setSizeError(true); return; }
    setSizeError(false);
    addItem({ ...product, price: selectedVariant?.price || product.price }, selectedSize, selectedColor, quantity, selectedVariant?.id || null);
    setJustAdded(true);
    setTimeout(() => setJustAdded(false), 1600);
  };

  const handleSelectSize = (s) => { setSelectedSize(s); setSizeError(false); };

  if (loading) return <div className="flex items-center justify-center min-h-screen bg-[#0A0A0A]"><Loader2 size={24} className="animate-spin text-[#8A8A8A]" /></div>;
  if (!product) return <NotFoundPage />;
  if (product.is_gift_card) return <GiftCardProduct product={product} />;

  const images = product.images || [];
  const price = selectedVariant?.price || product.price;
  const soldOut = product.available === false;
  const unavailable = soldOut || (selectedVariant && !selectedVariant.available);

  const setPieces = setPiecesFor(product, allProducts);
  const completeTheFit = allProducts.filter((p) => p.id !== id && !p.is_bundle && p.id !== "blackout-box-only-50" && !setPieces.includes(p)).slice(0, 4);
  const content = PRODUCT_CONTENT[id] || DEFAULT_CONTENT;
  // Jewelry and the Mystery Box ship from stock; everything else is made to order.
  const shipLine = shipsSameDay(product) ? SAME_DAY_SHIP_LINE : SHIP_LINE;
  // "Not protective gear" is about clothing only: not jewelry, not the Mystery Box.
  const isApparel = !isJewelry(product) && !isMysteryBox(product);

  const plainDescription = (content.details || product.description || "").replace(/\s+/g, " ").trim();
  // First sentence of the description (max ~120 chars), then the shipping line.
  const firstSentence = plainDescription.split(/(?<=\.)\s/)[0] || "";
  const lead = firstSentence.length > 120 ? `${firstSentence.slice(0, 120).replace(/[\s,;:]+\S*$/, "")}.` : firstSentence;
  const seoDescription = `${lead || `${product.name} by AERWEAR.`} ${shipLine}`;
  const productJsonLd = {
    "@context": "https://schema.org",
    "@type": "Product",
    name: product.name,
    image: images.slice(0, 5).map((u) => shopifyImg(u, 1200)),
    description: plainDescription || `${product.name} by AERWEAR.`,
    sku: id,
    brand: { "@type": "Brand", name: "AERWEAR" },
    offers: {
      "@type": "Offer",
      url: `${SITE_URL}/products/${id}`,
      price: Number(price || 0).toFixed(2),
      priceCurrency: "USD",
      availability: soldOut ? "https://schema.org/OutOfStock" : "https://schema.org/InStock",
      itemCondition: "https://schema.org/NewCondition",
    },
  };

  return (
    <div data-testid="product-detail-page" className="bg-[#0A0A0A] pb-24 md:pb-0">
      <SEO title={`${product.name} | AERWEAR`} description={seoDescription} image={images[0] ? shopifyImg(images[0], 1200) : undefined} type="product" product={product} path={`/products/${id}`} jsonLd={productJsonLd} />

      {/* Breadcrumb */}
      <div className="px-4 md:px-12 py-3 md:py-4">
        <Link to="/shop/men" className="inline-flex items-center gap-1 text-[10px] text-[#8A8A8A] hover:text-[#F2F0EB] uppercase tracking-[0.2em] transition-colors">
          <ChevronLeft size={12} /> Shop
        </Link>
      </div>

      {/* Mobile compact title + price (above gallery) */}
      <div className="md:hidden px-4 pb-4">
        <h1 data-testid="pdp-mobile-title" className="text-xl font-medium uppercase tracking-[0.15em] text-[#F2F0EB] leading-tight">{formatProductName(product)}</h1>
        <p data-testid="pdp-mobile-price" className="text-base text-[#F2F0EB] tracking-[0.1em] mt-1.5">${price?.toFixed(2)}</p>
      </div>

      {/* Product Section */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
        {/* Image Gallery */}
        <div className="bg-[#0A0A0A]">
          <div className="aspect-[4/5] md:aspect-auto md:h-[85vh] md:sticky md:top-0 overflow-hidden relative group border-b md:border-b-0 md:border-r border-[#8A8A8A]/20">
            {soldOut && (
              <div data-testid="pdp-sold-out" className="absolute inset-0 z-20 flex items-center justify-center bg-black/50 pointer-events-none">
                <div className="w-full bg-[#0A0A0A]/85 border-y border-[#F2F0EB]/25 py-4">
                  <span className="block text-center font-['Oswald'] text-3xl md:text-5xl font-bold uppercase tracking-[0.35em] text-[#F2F0EB]">Sold Out</span>
                </div>
              </div>
            )}
            {images.length > 0 ? (
              <>
                <img data-testid="product-main-image" src={shopifyImg(images[activeImage] || images[0], 1080)} srcSet={shopifySrcSet(images[activeImage] || images[0], [540, 720, 1080, 1440])} sizes={SIZES_PDP_MAIN} width="1080" height="1350" alt={productAlt(product)} onClick={() => setZoomOpen(true)} className="w-full h-full object-cover cursor-zoom-in" loading="eager" fetchPriority="high" />
                {images.length > 1 && (
                  <>
                    <button data-testid="image-prev" onClick={() => setActiveImage((p) => p === 0 ? images.length - 1 : p - 1)} className="absolute left-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-[#0A0A0A]/40 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"><ChevronLeft size={16} className="text-[#F2F0EB]" /></button>
                    <button data-testid="image-next" onClick={() => setActiveImage((p) => p === images.length - 1 ? 0 : p + 1)} className="absolute right-4 top-1/2 -translate-y-1/2 w-10 h-10 bg-[#0A0A0A]/40 backdrop-blur-sm flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity duration-300"><ChevronRight size={16} className="text-[#F2F0EB]" /></button>
                  </>
                )}
              </>
            ) : (
              <div className="flex items-center justify-center h-full text-[#8A8A8A]/30"><ShoppingBag size={64} strokeWidth={1} /></div>
            )}
          </div>
          {images.length > 1 && (
            <div className="flex gap-2 p-2 overflow-x-auto scrollbar-hide">
              {images.map((img, i) => (
                <button key={`thumb-${i}`} data-testid={`thumbnail-${i}`} onClick={() => setActiveImage(i)} className={`w-16 h-20 flex-shrink-0 overflow-hidden transition-all duration-300 border ${activeImage === i ? "border-[#F2F0EB]" : "border-transparent opacity-50 hover:opacity-80"}`}>
                  <img src={shopifyImg(img, 200)} alt={productAlt(product, `view ${i + 1}`)} width="64" height="80" className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Product Info */}
        <div className="px-6 md:px-12 lg:px-16 py-10 md:py-16 flex flex-col">
          <h1 data-testid="pdp-product-title" className="hidden md:block text-2xl md:text-3xl font-medium uppercase tracking-[0.15em] text-[#F2F0EB] leading-tight">{formatProductName(product)}</h1>
          <span data-testid="product-price" className="hidden md:block text-base md:text-lg text-[#F2F0EB] tracking-[0.1em] mt-5">${price?.toFixed(2)}</span>
          <p data-testid="pdp-stock-text" className="text-sm md:text-[15px] leading-relaxed text-[#D9D7D2] mt-2">{shipLine}</p>

          <div className="w-full h-px bg-[#8A8A8A]/20 my-8" />

          {/* Color */}
          {product.colors?.length > 0 && (
            <div className="mb-8">
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A] mb-3 block">Color: <span className="text-[#F2F0EB]">{selectedColor}</span></span>
              <div className="flex gap-3">
                {product.colors.map((c) => (
                  <button key={c.name} data-testid={`color-${c.name.toLowerCase()}`} onClick={() => setSelectedColor(c.name)}
                    className={`w-8 h-8 transition-all duration-300 ${selectedColor === c.name ? "ring-1 ring-[#F2F0EB] ring-offset-2 ring-offset-[#0A0A0A]" : "hover:ring-1 hover:ring-[#8A8A8A]"}`}
                    style={{ backgroundColor: c.hex }} title={c.name} />
                ))}
              </div>
            </div>
          )}

          {/* Size */}
          {product.sizes?.length > 0 && (
            <div className="mb-8">
              <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A] mb-3 block">Size</span>
              <div className="flex gap-2 flex-wrap">
                {product.sizes.map((s) => (
                  <button key={s} data-testid={`pdp-size-option-${s}`} onClick={() => handleSelectSize(s)}
                    className={`min-w-[48px] py-3 px-4 text-xs transition-colors duration-200 ${selectedSize === s ? "bg-[#F2F0EB] text-[#0A0A0A] border border-[#F2F0EB] font-medium" : "bg-transparent text-[#8A8A8A] border border-[#8A8A8A]/30 hover:border-[#F2F0EB] hover:text-[#F2F0EB]"}`}>{s}</button>
                ))}
              </div>
              {sizeError && <p data-testid="size-error" className="text-[11px] text-[#8A8A8A] mt-2">Please select a size</p>}
            </div>
          )}

          {/* Quantity */}
          <div className="mb-8">
            <span className="text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A] mb-3 block">Quantity</span>
            <div className="flex items-center border border-[#8A8A8A]/30 w-fit">
              <button data-testid="qty-decrease" onClick={() => setQuantity(Math.max(1, quantity - 1))} className="px-4 py-3 text-sm text-[#8A8A8A] hover:text-[#F2F0EB] transition-colors">-</button>
              <span className="px-5 py-3 text-sm text-[#F2F0EB] border-x border-[#8A8A8A]/30 min-w-[48px] text-center">{quantity}</span>
              <button data-testid="qty-increase" onClick={() => setQuantity(quantity + 1)} className="px-4 py-3 text-sm text-[#8A8A8A] hover:text-[#F2F0EB] transition-colors">+</button>
            </div>
          </div>

          {/* Add to Cart */}
          <div ref={atcRef}>
            <button data-testid="pdp-add-to-cart-button" onClick={handleAddToCart} disabled={unavailable}
              className="w-full bg-[#F2F0EB] text-[#0A0A0A] py-4 font-['Oswald'] text-xs uppercase tracking-[0.25em] font-semibold hover:bg-[#8A8A8A] transition-colors disabled:opacity-30 disabled:cursor-not-allowed">
              {unavailable ? "Sold Out" : "Add To Cart"}
            </button>
          </div>
          <PdpBuyExtras product={product} variant={selectedVariant} quantity={quantity} selectedSize={selectedSize} unavailable={unavailable} />

          {/* Collapsible sections */}
          <Accordion type="single" collapsible defaultValue="details" className="mt-8">
            <AccordionItem value="details" className="border-t border-[#8A8A8A]/20">
              <AccordionTrigger data-testid="pdp-accordion-details" className="py-4 text-xs uppercase tracking-[0.15em] text-[#F2F0EB] hover:no-underline">Details</AccordionTrigger>
              <AccordionContent className="text-xs text-[#8A8A8A] leading-relaxed pb-5">
                {content.details ? (
                  <p>{content.details}</p>
                ) : sanitizedDescription ? (
                  <div className="product-description" dangerouslySetInnerHTML={{ __html: sanitizedDescription }} />
                ) : (
                  <p>Premium AERWEAR piece in all black. Full details coming soon.</p>
                )}
                {isApparel && <p data-testid="pdp-not-protective" className="mt-3 text-[#D9D7D2]">{NOT_PROTECTIVE_LINE}</p>}
              </AccordionContent>
            </AccordionItem>

            {SHOW_SIZE_GUIDE && (
            <AccordionItem value="size-guide" className="border-t border-[#8A8A8A]/20">
              <AccordionTrigger data-testid="pdp-accordion-size-guide" className="py-4 text-xs uppercase tracking-[0.15em] text-[#F2F0EB] hover:no-underline">Size Guide</AccordionTrigger>
              <AccordionContent className="text-xs text-[#8A8A8A] leading-relaxed pb-5">
                <ul className="space-y-1.5">
                  {content.sizeGuide.map((line, i) => <li key={i}>{line}</li>)}
                </ul>
                <button data-testid="size-guide-toggle" onClick={() => setShowSizeGuide(true)} className="mt-3 underline text-[#F2F0EB] hover:text-[#8A8A8A] transition-colors uppercase tracking-[0.15em] text-[10px]">Open full size guide</button>
              </AccordionContent>
            </AccordionItem>
            )}

            <AccordionItem value="shipping" className="border-t border-[#8A8A8A]/20">
              <AccordionTrigger data-testid="pdp-accordion-shipping" className="py-4 text-xs uppercase tracking-[0.15em] text-[#F2F0EB] hover:no-underline">Shipping And Exchanges</AccordionTrigger>
              <AccordionContent className="text-xs text-[#8A8A8A] leading-relaxed pb-5">
                <p>{shipLine} {SHIPPING_TAIL}</p>
                <p className="mt-3">
                  Questions: <a href={`mailto:${CONTACT_EMAIL}`} className="underline text-[#D9D7D2]">{CONTACT_EMAIL}</a>.{" "}
                  <Link to="/policies/shipping" className="underline text-[#D9D7D2]">Shipping Policy</Link>{" · "}
                  <Link to="/policies/refunds" className="underline text-[#D9D7D2]">Exchanges and Refunds</Link>
                </p>
              </AccordionContent>
            </AccordionItem>

            <AccordionItem value="care" className="border-t border-b border-[#8A8A8A]/20">
              <AccordionTrigger data-testid="pdp-accordion-care" className="py-4 text-xs uppercase tracking-[0.15em] text-[#F2F0EB] hover:no-underline">Care</AccordionTrigger>
              <AccordionContent className="text-xs text-[#8A8A8A] leading-relaxed pb-5">
                <p>{content.care}</p>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </div>
      </div>

      <ShopTheSet pieces={setPieces} />

      {/* Complete The Fit */}
      {completeTheFit.length > 0 && (
        <section data-testid="complete-the-fit" className="px-6 md:px-12 py-16 md:py-24 border-t border-[#8A8A8A]/20">
          <h2 className="text-xl md:text-2xl font-medium uppercase tracking-[0.2em] text-[#F2F0EB] mb-10">Complete The Fit</h2>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-4 gap-y-10 sm:gap-x-6">
            {completeTheFit.map((p) => <ProductCard key={p.id} product={p} />)}
          </div>
        </section>
      )}

      <RecentlyViewed currentProductId={id} />

      {/* Desktop Sticky ATC */}
      {showStickyATC && (
        <div data-testid="sticky-atc-bar" className="hidden md:block fixed bottom-0 left-0 right-0 bg-[#0A0A0A]/95 backdrop-blur-xl border-t border-[#8A8A8A]/20 z-40">
          <div className="max-w-7xl mx-auto px-8 py-3 flex items-center justify-between gap-6">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-10 h-12 bg-[#141414] flex-shrink-0 overflow-hidden">{images[0] && <img src={shopifyImg(images[0], 120)} alt="" width="40" height="48" className="w-full h-full object-cover" loading="lazy" />}</div>
              <div className="min-w-0">
                <h3 className="font-['Oswald'] text-xs uppercase tracking-[0.12em] text-[#F2F0EB] truncate">{formatProductName(product)}</h3>
                <p className="text-xs text-[#8A8A8A]">${price?.toFixed(2)}{selectedSize ? ` · ${selectedSize}` : ""}</p>
              </div>
            </div>
            <button onClick={handleAddToCart} disabled={unavailable} className="flex-shrink-0 bg-[#F2F0EB] text-[#0A0A0A] px-8 py-3 font-['Oswald'] text-[10px] uppercase tracking-[0.25em] font-semibold hover:bg-[#8A8A8A] transition-colors disabled:opacity-30">
              {unavailable ? "Sold Out" : "Add To Cart"}
            </button>
          </div>
        </div>
      )}

      {/* Mobile Sticky ATC */}
      <div data-testid="mobile-sticky-bar" className="fixed bottom-0 left-0 right-0 bg-[#0A0A0A] border-t border-[#8A8A8A]/20 md:hidden z-40">
        {sizeError && <p data-testid="sticky-size-error" className="text-[11px] text-[#8A8A8A] text-center pt-2 -mb-1">Please select a size</p>}
        <div className="flex items-stretch gap-2 p-3">
          {product.sizes?.length > 0 && (
            <div className="flex gap-1.5 overflow-x-auto flex-shrink min-w-0 items-center scrollbar-hide">
              {product.sizes.map((s) => (
                <button key={s} data-testid={`sticky-size-${s}`} onClick={() => handleSelectSize(s)}
                  className={`flex-shrink-0 min-w-[38px] h-11 px-2 text-[12px] border transition-colors duration-200 ${selectedSize === s ? "bg-[#F2F0EB] text-[#0A0A0A] font-semibold border-[#F2F0EB]" : "bg-transparent text-[#8A8A8A] border-[#8A8A8A]/30 hover:border-[#F2F0EB] hover:text-[#F2F0EB]"}`}>{s}</button>
              ))}
            </div>
          )}
          <button data-testid="sticky-add-to-cart" onClick={handleAddToCart} disabled={unavailable}
            className={`flex-1 min-w-[130px] h-11 font-['Oswald'] text-[10px] uppercase tracking-[0.15em] font-semibold disabled:opacity-30 transition-colors duration-300 ${justAdded ? "bg-[#8A8A8A] text-[#0A0A0A] sticky-added-pulse" : "bg-[#F2F0EB] text-[#0A0A0A]"}`}>
            {unavailable ? "Sold Out" : (justAdded ? "Added \u2713" : `Add · $${(price * quantity).toFixed(2)}`)}
          </button>
        </div>
      </div>

      {/* Image Zoom */}
      {zoomOpen && images.length > 0 && (
        <div data-testid="image-zoom-overlay" className="fixed inset-0 z-[100] bg-[#0A0A0A] flex items-center justify-center" onClick={() => setZoomOpen(false)}>
          <button data-testid="image-zoom-close" onClick={() => setZoomOpen(false)} className="absolute top-5 right-5 z-10 w-10 h-10 flex items-center justify-center bg-[#F2F0EB]/10 text-[#F2F0EB]"><X size={20} /></button>
          <div className="w-full h-full overflow-auto flex items-center justify-center" style={{ touchAction: "pinch-zoom" }} onClick={(e) => e.stopPropagation()}>
            <img src={shopifyImg(images[activeImage] || images[0], 1400)} alt={productAlt(product)} width="1400" height="1750" className="max-w-none w-auto min-w-full md:min-w-0 md:max-w-[90vw] md:max-h-[90vh]" />
          </div>
          <p className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[10px] uppercase tracking-[0.2em] text-[#8A8A8A]">Pinch to zoom · Tap to close</p>
        </div>
      )}

      {SHOW_SIZE_GUIDE && <SizeGuideModal isOpen={showSizeGuide} onClose={() => setShowSizeGuide(false)} productName={product?.name} />}
    </div>
  );
}
